/**
 * src/pages/WhatIsConsciousness/sections/TheoriesComparison.tsx
 * TabExplorer comparing scientific and philosophical theories of consciousness:
 * GNWT, IIT, HOT, Predictive Processing, Physicalism, Dualism, Panpsychism, Illusionism,
 * with honest coverage of the 2023–25 Cogitate Consortium adversarial collaboration,
 * the 2023 IIT open letter controversy, and an explicit speculative note on Orch OR.
 */

import React from "react";
import { Network, Share2, Workflow, Sparkles, Layers, Scale, Compass, AlertTriangle } from "lucide-react";
import { TabExplorer } from "@/components/originBeyond/TabExplorer";
import { EvidenceBadge } from "@/components/originBeyond/EvidenceBadge";
import { Footnote } from "@/components/originBeyond/Footnote";
import type { TabItem } from "@/lib/originBeyond/types";
import "@/components/cosmic/cosmic.css";
export function TheoriesComparison() {
  const tabs: TabItem[] = [{
    id: "gnwt",
    label: "Global Workspace (GNWT)",
    icon: Network,
    level: "research",
    content: <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/30 pb-3">
            <h3 className="font-serif text-lg font-bold text-foreground">
              Global Neuronal Workspace Theory (Dehaene, Changeux, Baars)
            </h3>
            <div className="flex items-center gap-2">
              <EvidenceBadge level="research" />
              <Footnote n={5} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Core Premise
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Consciousness is a global broadcast. When local sensory processing crosses a threshold,
                long-range fronto-parietal axons "ignite", broadcasting the representation across the brain,
                making it globally available to memory, language, and motor planning.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Supporting Evidence
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Visual masking experiments: subliminal words activate early visual areas, but only consciously
                perceived words trigger a sharp, delayed (~300 ms, P3b wave) fronto-parietal ignition.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Main Criticisms
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Conflates consciousness itself with the cognitive access and introspective report required
                to answer experimenters. Frontal activations might be "post-perceptual" report rather
                than raw experience.
              </p>
            </div>
          </div>
        </div>
  }, {
    id: "iit",
    label: "Integrated Information (IIT)",
    icon: Share2,
    level: "research",
    content: <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/30 pb-3">
            <h4 className="font-serif text-lg font-bold text-foreground">
              Integrated Information Theory (Tononi, Koch)
            </h4>
            <div className="flex items-center gap-2">
              <EvidenceBadge level="research" />
              <Footnote n={6} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Core Premise
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Starts from phenomenology rather than function: consciousness is integrated information (Φ, phi).
                Any physical system possessing high causal feedback and differentiation is conscious to that
                exact degree, regardless of whether it performs a cognitive function.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Supporting Evidence
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                The Perturbational Complexity Index (PCI): zapping the brain with TMS and measuring EEG echo
                complexity reliably differentiates conscious states from deep sleep, anaesthesia, and vegetative states.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Criticisms & Controversies
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Highly contested: calculates astronomical mathematical complexity for even tiny grids. In 2023,
                a prominent group of researchers published an open letter calling IIT "pseudoscience" because its
                core identity claims may be untestable.
              </p>
            </div>
          </div>
        </div>
  }, {
    id: "predictive",
    label: "Predictive Processing / HOT",
    icon: Workflow,
    level: "research",
    content: <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/30 pb-3">
            <h4 className="font-serif text-lg font-bold text-foreground">
              Predictive Brain & Higher-Order Theories (Clark, Friston, Rosenthal, Lau)
            </h4>
            <div className="flex items-center gap-2">
              <EvidenceBadge level="research" />
              <Footnote n={4} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Predictive Engine
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                The brain is a hierarchical prediction machine constantly projecting top-down generative models
                of the environment. What we consciously experience is not raw sensory input, but the brain's
                best "controlled hallucination" constrained by incoming prediction errors.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Higher-Order Thought (HOT)
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                A mental state (e.g., visual perception of green) is only conscious when accompanied by a
                second, higher-order meta-representation ("I am currently perceiving green"). Locates consciousness
                in prefrontal metacognitive monitoring.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary block mb-1">
                Recurrent Processing (Lamme)
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Argues that conscious perception requires neither frontal ignition nor higher-order meta-cognition,
                but merely localized recurrent feedback loops within posterior sensory cortex.
              </p>
            </div>
          </div>
        </div>
  }, {
    id: "philosophy",
    label: "Philosophical Stances",
    icon: Scale,
    level: "philosophy",
    content: <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/30 pb-3">
            <h4 className="font-serif text-lg font-bold text-foreground">
              Ontological Positions on Mind and Matter
            </h4>
            <div className="flex items-center gap-2">
              <EvidenceBadge level="philosophy" />
              <Footnote n={9} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-3.5 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary font-bold block mb-1">
                Physicalism
              </span>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Everything that exists is physical. Consciousness is either identical to neurochemical activity
                or an emergent high-level macro-property of complex biological computing.
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary font-bold block mb-1">
                Dualism
              </span>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Mind and physical matter are fundamentally distinct substances (Cartesian substance dualism) or
                physical matter has both physical and non-physical qualitative properties (property dualism).
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary font-bold block mb-1">
                Panpsychism
              </span>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Subjective experience is a fundamental feature of the universe, like mass or charge. Basic physical
                entities possess proto-experiential properties that aggregate in complex biological brains.
              </p>
            </div>

            <div className="p-3.5 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-primary font-bold block mb-1">
                Illusionism
              </span>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Dennett, Frankish: Qualia do not exist. The brain constructs a simplified, introspective user-illusion
                of private qualitative sensations to monitor its own cognitive states.
              </p>
            </div>
          </div>
        </div>
  }, {
    id: "quantum",
    label: "Quantum Mind (Orch OR)",
    icon: Compass,
    level: "speculative",
    content: <div className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-border/30 pb-3">
            <h4 className="font-serif text-lg font-bold text-foreground">
              Orchestrated Objective Reduction (Penrose & Hameroff)
            </h4>
            <div className="flex items-center gap-2">
              <EvidenceBadge level="speculative" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-card/50 border border-border/40">
              <span className="font-mono text-xs uppercase tracking-wider text-destructive block mb-1 font-bold">
                Hypothesis Overview
              </span>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Proposes that consciousness originates at the quantum level inside neuronal microtubules.
                Roger Penrose argued that Gödelian mathematical intuition is non-computable, requiring gravity-induced
                quantum wave-function collapse ("objective reduction") orchestrated by biological structures.
              </p>
            </div>

            <div className="p-4 rounded-lg bg-destructive/10 border border-destructive/30">
              <span className="font-mono text-xs uppercase tracking-wider text-destructive block mb-1 font-bold flex items-center gap-1.5">
                <AlertTriangle className="size-3.5 text-destructive" />
                <span>Status: Highly Speculative & Contested</span>
              </span>
              <p className="text-xs sm:text-sm text-destructive-foreground leading-relaxed font-sans">
                Mainstream neurobiology and physics remain deeply skeptical. The mammalian brain is a "warm, wet,
                and noisy" environment where quantum coherence typically decoheres on femtosecond timescales—orders
                of magnitude faster than neuronal firing.
                <strong>
                  {" "}Quantum physics is not scientific proof that consciousness survives death or that thought
                  directly creates external physical reality.
                </strong>
              </p>
            </div>
          </div>
        </div>
  }];
  return <section className="w-full max-w-full min-w-0" aria-labelledby="theories-comparison-heading">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="size-1.5 rounded-[1px] bg-primary rotate-45 shadow-[0_0_8px_1px_var(--glow-primary)] shrink-0" aria-hidden="true" />
            <span className="ql-eyebrow">Competing Frameworks</span>
          </div>
          <h2 id="theories-comparison-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            Theories of Consciousness
          </h2>
        </div>
        <p className="font-sans text-xs sm:text-sm text-muted-foreground max-w-md">
          Cognitive scientists and philosophers disagree on whether consciousness is global broadcasting,
          integrated causal information, or an introspective illusion.
        </p>
      </div>

      <TabExplorer tabs={tabs} ariaLabel="Theories of consciousness comparison tabs" defaultId="gnwt" />

      {/* Adversarial Collaboration Note */}
      <div className="mt-6 p-4 rounded-xl border border-primary/30 bg-primary/5 flex items-start gap-3">
        <Layers className="size-4 text-primary shrink-0 mt-0.5" />
        <p className="font-sans text-xs sm:text-sm text-muted-foreground leading-relaxed break-words">
          <strong className="text-foreground">Adversarial Collaboration (Cogitate Consortium):</strong> In 2023–25,
          advocates of GNWT and IIT agreed in advance on open protocols and pre-registered empirical tests.
          Early results challenged predictions of both models: IIT's posterior synchronization was weaker than predicted,
          and GNWT's frontal ignition did not consistently appear for simple visual contents.
          Science advances by putting competing theories to shared tests, even when the results leave the question open.
        </p>
      </div>
    </section>;
}